import {generatePath, NavigateOptions, useNavigate, useSearchParams} from 'react-router-dom';

import {mergeSearchParams} from '@zzng/common/utils/common';

// URL 경로에서 동적 매개변수를 추출하여 객체 형태로 만듭니다.
// 예시:
// type Params1 = PathParams<'/user/:id/posts/:postId'>;  결과: { id: string; postId: string; }
// type Params2 = PathParams<'/product/:productId'>;      결과: { productId: string; }
type PathParams<T extends string> = T extends `${string}:${infer Param}/${infer Rest}`
  ? {[K in Param | keyof PathParams<Rest>]: string}
  : T extends `${string}:${infer Param}`
  ? {[K in Param]: string}
  : {};

export type ExtendedNavigateOptions<T extends string> = NavigateOptions & {
  pathParams?: PathParams<T>;
  keepSearchParams?: boolean;
  /** 웹뷰에서만 가능한 옵션, 관련이슈: https://jch.github.io/turbo-cancel-view-transition */
  transitionDirection?: 'forward' | 'back';
};

// useNavigate의 기능을 확장하여 다양한 형태의 인자를 받을 수 있게 합니다.
// 예시:
// navigate(-1);  뒤로 가기
// navigate('/user/:id', { pathParams: { id: '123' } });  동적 경로로 이동
// navigate({ pathname: '/search', search: '?query=react' });  객체 형태로 이동
type ExtendedNavigateFunction = {
  (to: number): void;
  <T extends string>(to: T, options?: ExtendedNavigateOptions<T>): void;
  (to: Partial<Location>, options?: ExtendedNavigateOptions<string>): void;
};

const useAdvancedNavigate = (): ExtendedNavigateFunction => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const DATA_TRANSITION_DIRECTION = 'data-transition-direction';

  const navigateWithTransition = (navigateFn: () => void, transitionDirection?: 'forward' | 'back') => {
    if (transitionDirection && 'startViewTransition' in document) {
      document.documentElement.setAttribute(DATA_TRANSITION_DIRECTION, transitionDirection);
      const transition = document.startViewTransition(navigateFn);

      transition.finished.then(() => {
        document.documentElement.removeAttribute(DATA_TRANSITION_DIRECTION);
      });
    } else {
      navigateFn();
    }
  };

  const advancedNavigate: ExtendedNavigateFunction = (
    to: number | string | Partial<Location>,
    options?: ExtendedNavigateOptions<string>
  ) => {
    if (typeof to === 'number') {
      navigate(to);
      return;
    }

    if (typeof to === 'object') {
      let {search, pathname} = to;

      if (options?.pathParams && !!pathname) {
        pathname = generatePath(pathname, options.pathParams);
      }

      if (options?.keepSearchParams) {
        search = mergeSearchParams(searchParams, search);
      }

      navigateWithTransition(() => navigate({hash: to.hash, pathname, search}, options), options?.transitionDirection);

      return;
    }

    if (typeof to === 'string') {
      let [path, queryString] = to.split('?');

      if (options?.pathParams) {
        path = generatePath(path, options.pathParams);
      }

      if (options?.keepSearchParams) {
        queryString = mergeSearchParams(searchParams, queryString);
      }

      const fullPath = queryString ? `${path}?${queryString}` : path;

      navigateWithTransition(() => navigate(fullPath, options), options?.transitionDirection);
    }
  };

  return advancedNavigate;
};

export default useAdvancedNavigate;
